import { useState } from "react";
import { useFieldArray, type Control } from "react-hook-form";
import { Pencil, Trash } from "lucide-react";
import { Button } from "@stackje/shared-components/ui/button";
import { Dialog, DialogTrigger } from "@stackje/shared-components/ui/dialog";
import { SocialMediaEditFormDialogContent } from "./social-media-edit-form-dialog-content";
import { type SocialMediaLink } from "@stackje/common/config";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { 
  faYoutube,
  faInstagram,
  faTiktok,
  faXTwitter,
  faPinterest,
  faWhatsapp,
  faFacebook,
  faLinkedin,
  faReddit,
  faDiscord,
  faTelegram,
  faSnapchat,
  faGithub,
  faGitlab,
  faTwitch,
  faVimeo,
  faMedium,
  faSpotify,
  faSoundcloud,
  faDribbble,
  faBehance,
  faMastodon,
  faWeixin,
  faLine,
  faVk,
} from "@fortawesome/free-brands-svg-icons";

type SocialMediaLinkFormData = {
  platform: SocialMediaLink["platform"];
  url: string;
  displayName?: string;
};

interface SocialLinksFieldProps {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: Control<any>;
  name: string;
  fieldArrayName?: string;
}

const getSocialIcon = (platform: string) => {
  switch (platform) {
    case 'email':
      return faEnvelope;
    case 'youtube':
      return faYoutube;
    case 'instagram':
      return faInstagram;
    case 'tiktok':
      return faTiktok;
    case 'x':
      return faXTwitter;
    case 'pinterest':
      return faPinterest;
    case 'whatsapp':
      return faWhatsapp;
    case 'facebook':
      return faFacebook;
    case 'linkedin':
      return faLinkedin;
    case 'reddit':
      return faReddit;
    case 'discord':
      return faDiscord;
    case 'telegram':
      return faTelegram;
    case 'snapchat':
      return faSnapchat;
    case 'github':
      return faGithub;
    case 'gitlab':
      return faGitlab;
    case 'twitch':
      return faTwitch;
    case 'vimeo':
      return faVimeo;
    case 'medium':
      return faMedium;
    case 'spotify':
      return faSpotify;
    case 'soundcloud':
      return faSoundcloud;
    case 'dribbble':
      return faDribbble;
    case 'behance':
      return faBehance;
    case 'mastodon':
      return faMastodon;
    case 'weixin':
      return faWeixin;
    case 'line':
      return faLine;
    case 'vk':
      return faVk;
    default:
      return faEnvelope;
  }
};

const getPlatformLabel = (platform: string) => {
  switch (platform) {
    case 'email':
      return 'Email';
    case 'youtube':
      return 'YouTube';
    case 'instagram':
      return 'Instagram';
    case 'tiktok':
      return 'TikTok';
    case 'x':
      return 'X (Twitter)';
    case 'pinterest':
      return 'Pinterest';
    case 'whatsapp':
      return 'WhatsApp';
    case 'facebook':
      return 'Facebook';
    case 'linkedin':
      return 'LinkedIn';
    case 'reddit':
      return 'Reddit';
    case 'discord':
      return 'Discord';
    case 'telegram':
      return 'Telegram';
    case 'snapchat':
      return 'Snapchat';
    case 'github':
      return 'GitHub';
    case 'gitlab':
      return 'GitLab';
    case 'twitch':
      return 'Twitch';
    case 'vimeo':
      return 'Vimeo';
    case 'medium':
      return 'Medium';
    case 'spotify':
      return 'Spotify';
    case 'soundcloud':
      return 'SoundCloud';
    case 'dribbble':
      return 'Dribbble';
    case 'behance':
      return 'Behance';
    case 'mastodon':
      return 'Mastodon';
    case 'weixin':
      return 'WeChat';
    case 'line':
      return 'LINE';
    case 'vk':
      return 'VK';
    default:
      return platform;
  }
};

export function SocialLinksField({ control, name }: SocialLinksFieldProps) {
  const { fields, append, update, remove } = useFieldArray({
    control,
    name,
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingLinkIndex, setEditingLinkIndex] = useState<number | null>(null);
  const [editingLinkData, setEditingLinkData] = useState<SocialMediaLinkFormData | undefined>(undefined);

  const handleLinkSubmit = (data: SocialMediaLinkFormData) => {
    if (editingLinkIndex !== null) {
      // Edit existing link
      update(editingLinkIndex, data);
    } else {
      // Add new link
      append(data);
    }
    // Reset form
    setEditingLinkIndex(null);
    setEditingLinkData(undefined);
    setDialogOpen(false);
  };

  const handleOpenChangeDialog = (open: boolean) => {
    setDialogOpen(open);
    if (!open) {
      setEditingLinkIndex(null);
      setEditingLinkData(undefined);
    }
  };

  return (
    <div className="border-t pb-2 pt-4">
      <h3 className="mb-2">Social Media Links</h3>
      {fields.map((field, index) => {
        const socialLink = field as unknown as SocialMediaLink;
        return (
          <div
            key={field.id}
            className="space-y-2 border-2 shadow-sm px-4 py-2 rounded-md mb-4 hover:bg-gray-50 flex justify-between items-center"
          >
            <div className="flex items-center gap-2">
              <FontAwesomeIcon
                icon={getSocialIcon(socialLink.platform)}
                className="w-5 h-5"
              />
              <div>
                <p className="font-medium">{getPlatformLabel(socialLink.platform)}</p>
                <p className="text-sm text-gray-600">
                  {socialLink.displayName || socialLink.url}
                </p>
              </div>
            </div>
            <div className="flex items-center">
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Edit social media link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  setEditingLinkIndex(index);
                  setEditingLinkData({
                    platform: socialLink.platform,
                    url: socialLink.url,
                    displayName: socialLink.displayName,
                  });
                  setDialogOpen(true);
                }}
              >
                <Pencil className="h-4 w-4 mb-2" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon"
                aria-label="Delete social media link"
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  remove(index);
                }}
              >
                <Trash className="h-4 w-4 ml-2 mb-2" />
              </Button>
            </div>
          </div>
        );
      })}

      <div className="">
        <Dialog open={dialogOpen} onOpenChange={handleOpenChangeDialog}>
          <DialogTrigger asChild>
            <Button
              type="button"
              variant="outline"
              className="w-full"
              onClick={() => {
                setEditingLinkIndex(null);
                setEditingLinkData(undefined);
              }}
            >
              Add social media link
            </Button>
          </DialogTrigger>
          <SocialMediaEditFormDialogContent
            key={`social-dialog-${editingLinkIndex ?? 'new'}-${dialogOpen}`}
            isEditing={editingLinkIndex !== null}
            initialData={editingLinkData}
            onSubmit={handleLinkSubmit}
            editingLinkIndex={editingLinkIndex}
            linksLength={fields.length}
            existingPlatforms={fields.map(f => (f as unknown as SocialMediaLink).platform)}
          />
        </Dialog>
      </div>

    </div>
  );
}
